import React, { useState, useRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { TaskData, TaskModalState } from "@/entities/tasks/types";
import Text from "@/components/ui/text";
import { useUsers, useUsersMe } from "@/entities/users/hooks";
import { getRelativeTime } from "@/utils/datetime";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { cn } from "@/utils/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import CreatedByDreamerIndication from "@/components/controls/Task/CreatedByDreamerIndication";
import { useTaskActions } from "@/services/hooks/entities/tasks/useTaskActions";
import Checkmark from "@/components/ui/checkmark";
import AttachmentsIndicator from "@/components/controls/Task/AttachmentsIndicator";
import AssigneeIndicator from "@/components/controls/Task/AssigneeIndicator";
import RemindersIndicator from "@/components/controls/Task/RemindersIndicator";
import HighPriorityIndicator from "@/components/controls/Task/HighPriorityIndicator";
import DueDateIndicator from "@/components/controls/Task/DueDateIndicator";
import CreatedByUserIndication from "@/components/controls/Task/CreatedByUserIndication";
import { isNull } from "lodash";

const TRANSLATION_KEY = "tasks.drawer.task";

interface Props {
  task: TaskData;
  setTaskModalState: React.Dispatch<React.SetStateAction<TaskModalState>>;
  isCompletedTasksView: boolean;
}

const Task: React.FC<Props> = ({
  task,
  setTaskModalState,
  isCompletedTasksView
}) => {
  const { t } = useTranslation();
  const {
    id,
    title,
    assigneeId,
    description,
    dueDate,
    reminders,
    isHighPriority,
    attachments,
    isCreatedByDreamer,
    completedBy,
    completedAt,
    createdAt,
    createdBy
  } = task;

  const {
    localIsCompleted,
    showStrikethrough,
    isAnimating,
    shouldHide,
    isDeleteTaskModalOpen,
    setIsDeleteTaskModalOpen,
    handleCheckedChange,
    handleDeleteTask
  } = useTaskActions({ task });

  const [assigneeData] = useUsers([assigneeId])?.users;
  const [createdByUserData] = useUsers([createdBy || ""]).users;
  const [completedByData] = useUsers([completedBy ?? ""]).users;
  const userData = useUsersMe().data;
  const completedByName =
    completedByData?.id === userData?.id
      ? t("you")
      : completedByData?.displayName;

  // Pre-resolve names for the Dreamer chip so the indicator doesn't need its
  // own useUsers subscriptions (which cause re-renders on every user-store update).
  const dreamerContextOwnerName =
    isCreatedByDreamer && createdBy
      ? createdByUserData?.displayName ?? null
      : null;
  const dreamerAssigneeName =
    assigneeData?.id === userData?.id ? t("you") : assigneeData?.displayName ?? null;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const taskRef = useRef<HTMLDivElement>(null);

  // The same task can mount twice per page (open-tasks widget and tasks drawer)
  const titleId = useId();

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const getDropdownOptions = () => {
    return {
      edit: () =>
        setTaskModalState({
          editTaskId: id,
          editTaskData: task,
          isEditMode: true,
          isOpen: true
        }),
      delete: () => setIsDeleteTaskModalOpen(true)
    };
  };

  if (shouldHide) {
    return null;
  }

  return (
    <div
      ref={taskRef}
      data-test={`tasks-item-${id}`}
      className={cn(
        "group flex p-2 pl-1 cursor-pointer transition-colors duration-200 ease-in-out rounded-xl",
        isAnimating && "animate-bounce-out-down",
        isDropdownOpen ? "bg-white-4" : "hover:bg-white-4"
      )}
    >
      <div
        className="flex flex-col gap-[2px] flex-1 min-w-0"
        onClick={() =>
          setTaskModalState({ editTaskId: id, editTaskData: task, isEditMode: true, isOpen: true })
        }
      >
        <div className="flex items-start gap-2 w-full min-w-0">
          <Checkmark
            id={id}
            aria-labelledby={titleId}
            checked={localIsCompleted}
            onCheckedChange={handleCheckedChange}
            disabled={isAnimating}
            tooltipText={t(
              `${TRANSLATION_KEY}.${localIsCompleted ? "markAsOpen" : "markAsComplete"}`
            )}
          />
          {/* Title sits outside Checkmark so clicking it opens the edit modal instead of toggling completion */}
          <Text
            id={titleId}
            variant="label"
            className="min-w-0 break-words pt-1.5"
          >
            {title ?? ""}
          </Text>
        </div>
        <div className="pl-9 flex flex-col gap-1">
          <Tooltip>
            <TooltipTrigger className="text-start">
              <Text
                variant="bodySecondary"
                className={cn(
                  "line-clamp-2",
                  showStrikethrough && "opacity-60"
                )}
              >
                {description}
              </Text>
            </TooltipTrigger>
            {description && description?.length > 100 && (
              <TooltipContent maxWidth={20}>
                <div className="line-clamp-[8] overflow-hidden">
                  {description}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
          <div className="flex gap-1">
            <AssigneeIndicator assigneeData={assigneeData} />
            <DueDateIndicator
              dueDate={dueDate}
              tooltip={t("tasks.drawer.task.tooltips.dueDate")}
            />
            {!isNull(reminders) && reminders?.items?.length > 0 && (
              <RemindersIndicator reminders={reminders} />
            )}
            {isHighPriority && (
              <HighPriorityIndicator
                tooltip={t("tasks.drawer.task.tooltips.highPriority")}
              />
            )}
            {!isNull(attachments) && attachments?.items?.length > 0 && (
              <AttachmentsIndicator attachments={attachments} />
            )}
          </div>
          {isCreatedByDreamer && !isNull(createdAt) && (
            <CreatedByDreamerIndication
              task={task}
              contextOwnerName={dreamerContextOwnerName}
              contextOwnerUserData={createdByUserData ?? null}
              assigneeName={dreamerAssigneeName}
            />
          )}
          {!isCreatedByDreamer &&
            createdBy &&
            createdBy !== userData?.id &&
            createdByUserData && (
              <CreatedByUserIndication createdByUserData={createdByUserData} />
            )}
          {isCompletedTasksView &&
            !isNull(completedByData) &&
            !isNull(completedAt) && (
              <Text variant="smBodyTertiary">
                {`${t(`${TRANSLATION_KEY}.completed`)} ${getRelativeTime(new Date(completedAt))} ${t(`${TRANSLATION_KEY}.by`)} ${completedByName}`}
              </Text>
            )}
        </div>
      </div>
      <OptionsDropdownMenu
        options={getDropdownOptions()}
        tPrefix={`${TRANSLATION_KEY}.options`}
        className={cn(
          "shrink-0 w-7 h-7 rounded-lg transition-opacity duration-200 ease-in-out",
          isDropdownOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )}
        iconSize="md"
        onOpenChange={handleDropdownOpenChange}
      />
      <ConfirmationModal
        isOpen={isDeleteTaskModalOpen && !isDropdownOpen}
        setIsOpen={setIsDeleteTaskModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteConfirmation.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY}.deleteConfirmation.confirmation`
        )}
        handleSubmit={handleDeleteTask}
      />
    </div>
  );
};

export default Task;
